import React from "react";

import FirebaseAdapter from "@wc/adapters/firebase";
import { listTeamMembers } from "@wc/domain/team-membership";

/**
 * Hook to get team members' goals with polling updates
 * Now uses callable functions for security
 * @param {string} teamId The team ID
 * @param {string} organizationId The organization ID
 * @param {string} weekOf Week identifier (yyyy-MM-dd)
 * @returns {{membersGoals: Array, loading: boolean, error: Error|null, refetch: () => Promise<void>}} Team goals data
 */
export function useTeamGoals(teamId, organizationId, weekOf) {
  const [membersGoals, setMembersGoals] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchTeamGoals = React.useCallback(async () => {
    if (!teamId || !organizationId || !weekOf) {
      setMembersGoals([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const currentUser = FirebaseAdapter().authn.getCurrentUser();
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      const members = await listTeamMembers(teamId, currentUser.uid);

      if (!members || members.length === 0) {
        setMembersGoals([]);
        setLoading(false);
        return;
      }

      const userIds = members.map((m) => m.id);

      // Fetch goals and weeks using callable functions (server-side validation)
      const [goals, weeks] = await Promise.all([
        FirebaseAdapter().goals.listTeamGoalsForWeek(
          userIds,
          organizationId,
          weekOf,
          teamId,
        ),
        FirebaseAdapter().goals.listTeamGoalWeeks(
          userIds,
          organizationId,
          weekOf,
          teamId,
        ),
      ]);

      // Combine data
      const goalsByUser = goals.reduce((acc, goal) => {
        if (!acc[goal.userId]) {
          acc[goal.userId] = [];
        }
        acc[goal.userId].push(goal);
        return acc;
      }, {});

      const weeksByUser = weeks.reduce((acc, week) => {
        acc[week.userId] = week;
        return acc;
      }, {});

      const teamGoals = members.map((member) => {
        const userGoals = goalsByUser[member.id] || [];
        const weekSettings = weeksByUser[member.id];
        const completedGoals = userGoals.filter((g) => g.completed);

        return {
          userId: member.id,
          displayName: member.displayName,
          photoURL: member.photoURL || null,
          goals: userGoals,
          progress: {
            completed: completedGoals.length,
            total: Math.min(userGoals.length, 5),
          },
          isLocked: weekSettings?.lockedAt != null,
          lockedAt: weekSettings?.lockedAt || null,
        };
      });

      setMembersGoals(teamGoals);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch team goals:", err);
      setError(err);
      setLoading(false);
    }
  }, [teamId, organizationId, weekOf]);

  React.useEffect(() => {
    fetchTeamGoals();
  }, [fetchTeamGoals]);

  return { membersGoals, loading, error, refetch: fetchTeamGoals };
}
